import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import {
  Upload,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  X,
  Loader2,
} from "lucide-react";
import { KairosLogo } from "./KairosLogo";
import { cardSurface, cardSubtle, cardSuccess } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import {
  listEquipment,
  uploadEquipmentDataPreview,
  confirmEquipmentDataUpload,
  getEquipmentKBDefinition,
  type Equipment,
  type UploadPreviewOut,
} from "@/lib/api/building";
import { useEquipmentTypeMeta } from "@/lib/equipment-type-meta";
import { UnmatchedColumnRemap } from "@/components/building/datapoints/UnmatchedColumnRemap";
import {
  equipmentCandidateGroups,
  type RemapCandidateGroup,
} from "@/lib/kb-remap-groups";

type DataContext = "baseline" | "operational";

/** Equipment-only import target for Mittausdatan tuonti (story #1140). */
type EquipmentTarget = {
  id: string;
  name: string;
};

export function DataIntakePanel({
  children,
  buildingId,
  buildingName,
  onImported,
}: {
  children: React.ReactNode;
  buildingId: string;
  buildingName: string;
  /** Called after a successful import — lets the Signaalit list refetch. */
  onImported?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden"
      >
        <SheetHeader className="px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <KairosLogo size={28} />
            <div className="flex-1 min-w-0 text-left">
              <SheetTitle className="text-sm font-semibold">
                Mittausdatan tuonti
              </SheetTitle>
              <SheetDescription className="text-[11px] text-muted-foreground">
                {buildingName}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ImportFlow buildingId={buildingId} onImported={onImported} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ImportFlow({
  buildingId,
  onImported,
}: {
  buildingId: string;
  onImported?: () => void;
}) {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [target, setTarget] = useState<EquipmentTarget | null>(null);

  useEffect(() => {
    listEquipment(buildingId)
      .then(setEquipment)
      .catch(() => {});
  }, [buildingId]);

  return (
    <div className="space-y-4">
      <TargetPicker
        equipment={equipment}
        target={target}
        onChange={setTarget}
      />
      {target ? (
        <SignalIntakeFlow
          key={target.id}
          buildingId={buildingId}
          target={target}
          onImported={onImported}
        />
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Valitse talotekniikka — tuo mittausdata yhdelle laitteelle kerrallaan.
          Signaalien käyttöön otto tapahtuu Signaalit-välilehdellä.
        </p>
      )}
    </div>
  );
}

function TargetPicker({
  equipment,
  target,
  onChange,
}: {
  equipment: Equipment[];
  target: EquipmentTarget | null;
  onChange: (target: EquipmentTarget | null) => void;
}) {
  const eqMeta = useEquipmentTypeMeta();

  function handleChange(value: string) {
    if (!value) {
      onChange(null);
      return;
    }
    const item = equipment.find((e) => e.id === value);
    if (item) onChange({ id: item.id, name: item.name });
  }

  return (
    <Field label="Valitse talotekniikka">
      <select
        value={target?.id ?? ""}
        onChange={(e) => handleChange(e.target.value)}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-primary/60"
      >
        <option value="">— Valitse laite —</option>
        {equipment.map((eq) => (
          <option key={eq.id} value={eq.id}>
            {eqMeta[eq.equipment_type]?.icon ?? "⚙️"} {eq.name}
            {eq.equipment_tag ? ` (${eq.equipment_tag})` : ""}
          </option>
        ))}
      </select>
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        Kartoi CSV-sarakkeet laitteen signaaleihin ja tallenna historia. Ei avaa
        signaaleja käyttöön — se tehdään Signaalit-listalta (Signaalien
        aktivointi).
      </p>
    </Field>
  );
}

function DataContextPicker({
  value,
  onChange,
}: {
  value: DataContext;
  onChange: (ctx: DataContext) => void;
}) {
  return (
    <Field label="Datan tyyppi">
      <div className="grid grid-cols-2 gap-2">
        {(["baseline", "operational"] as const).map((ctx) => {
          const active = value === ctx;
          return (
            <button
              key={ctx}
              type="button"
              onClick={() => onChange(ctx)}
              className={`text-left rounded-md border px-2.5 py-2 transition ${
                active
                  ? "border-primary/60 bg-primary/5"
                  : "border-border bg-surface hover:border-primary/30"
              }`}
            >
              <div
                className={`text-xs font-medium ${active ? "text-primary" : "text-foreground"}`}
              >
                {ctx === "baseline" ? "Perusdata" : "Käyttödata"}
              </div>
              <div className="mt-0.5 text-[10px] text-muted-foreground">
                {ctx === "baseline"
                  ? "Vertailupohja, ennen optimointia"
                  : "Operatiivinen data"}
              </div>
            </button>
          );
        })}
      </div>
    </Field>
  );
}

/**
 * Equipment CSV import: map columns + write history only (ADR-017). Declare /
 * kyvykkyys activation live on Signaalit → Signaalien aktivointi — not here (story #1140).
 */
function SignalIntakeFlow({
  buildingId,
  target,
  onImported,
}: {
  buildingId: string;
  target: EquipmentTarget;
  onImported?: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<UploadPreviewOut | null>(null);
  const [phase, setPhase] = useState<
    "select" | "previewing" | "review" | "importing"
  >("select");
  const [error, setError] = useState<string | null>(null);
  const [rowsWritten, setRowsWritten] = useState<number | null>(null);
  const [importedPoints, setImportedPoints] = useState<string[]>([]);
  const [dataContext, setDataContext] = useState<DataContext>("baseline");
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set(),
  );
  // csv_column -> canonical_id for unmatched columns the operator remapped.
  const [remapAssignments, setRemapAssignments] = useState<
    Record<string, string>
  >({});
  // null while loading or after the KB catalog fetch failed — the remap UI
  // degrades to the pre-existing read-only "Ohitetut sarakkeet" list.
  const [candidateGroups, setCandidateGroups] = useState<
    RemapCandidateGroup[] | null
  >(null);

  async function handleFileSelected(f: File | null) {
    setFile(f);
    setPreview(null);
    setError(null);
    setRowsWritten(null);
    setImportedPoints([]);
    setRemapAssignments({});
    setCandidateGroups(null);
    if (!f) {
      setPhase("select");
      return;
    }
    setPhase("previewing");
    try {
      const result = await uploadEquipmentDataPreview(buildingId, target.id, f);
      setPreview(result);
      setSelectedColumns(new Set(result.matched.map((m) => m.csv_column)));
      setPhase("review");
      getEquipmentKBDefinition(buildingId, target.id)
        .then(equipmentCandidateGroups)
        .then((groups) => setCandidateGroups(groups))
        .catch(() => setCandidateGroups(null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Esikatselu epäonnistui");
      setPhase("select");
    }
  }

  function assignUnmatchedColumn(
    csvColumn: string,
    canonicalId: string | null,
  ) {
    setRemapAssignments((prev) => {
      if (canonicalId === null) {
        const next = { ...prev };
        delete next[csvColumn];
        return next;
      }
      return { ...prev, [csvColumn]: canonicalId };
    });
  }

  function toggleColumn(csvColumn: string) {
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(csvColumn)) next.delete(csvColumn);
      else next.add(csvColumn);
      return next;
    });
  }

  async function handleConfirm() {
    if (!file || !preview) return;
    const selectedMatches = preview.matched.filter((m) =>
      selectedColumns.has(m.csv_column),
    );
    const schema = {
      ...Object.fromEntries(
        selectedMatches.map((m) => [m.csv_column, m.canonical_id]),
      ),
      ...remapAssignments,
    };
    setPhase("importing");
    setError(null);
    try {
      const result = await confirmEquipmentDataUpload(
        buildingId,
        target.id,
        file,
        schema,
        true,
        dataContext,
      );
      setRowsWritten(result.rows_written);
      setImportedPoints(result.imported_data_points);
      setPhase("select");
      setRemapAssignments({});
      setCandidateGroups(null);
      onImported?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tuonti epäonnistui");
      setPhase("review");
    }
  }

  if (phase === "previewing" || phase === "importing") {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-xs">
          {phase === "previewing"
            ? "Tunnistetaan signaaleja…"
            : `Tuodaan dataa kohteeseen ${target.name}…`}
        </span>
      </div>
    );
  }

  if (phase === "review" && preview) {
    return (
      <SignalPreviewView
        fileName={file?.name ?? ""}
        preview={preview}
        targetName={target.name}
        dataContext={dataContext}
        onDataContextChange={setDataContext}
        error={error}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        candidateGroups={candidateGroups}
        remapAssignments={remapAssignments}
        onAssignUnmatchedColumn={assignUnmatchedColumn}
        onConfirm={handleConfirm}
        onCancel={() => handleFileSelected(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataContextPicker value={dataContext} onChange={setDataContext} />

      <Field label="Tiedosto (CSV)">
        <FileDrop file={file} onFile={handleFileSelected} accept=".csv" />
      </Field>

      {error && (
        <div className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {rowsWritten != null && (
        <div className={cn(cardSuccess, "p-3 space-y-1.5")}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
            <span className="text-xs font-medium text-foreground">
              Data tuotu laitteeseen {target.name}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {rowsWritten.toLocaleString("fi-FI")} mittausta tallennettu (
            {dataContext === "baseline" ? "Perusdata" : "Käyttödata"}).
          </p>
          {importedPoints.length > 0 && (
            <p className="text-[11px] text-foreground border-t border-success/20 pt-1.5">
              {importedPoints.length === 1
                ? "Vahvistettu signaali on nyt käytössä."
                : `${importedPoints.length} vahvistettua signaalia on nyt käytössä.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SignalPreviewView({
  fileName,
  preview,
  targetName,
  dataContext,
  onDataContextChange,
  error,
  selectedColumns,
  onToggleColumn,
  candidateGroups,
  remapAssignments,
  onAssignUnmatchedColumn,
  onConfirm,
  onCancel,
}: {
  fileName: string;
  preview: UploadPreviewOut;
  targetName: string;
  dataContext: DataContext;
  onDataContextChange: (ctx: DataContext) => void;
  error: string | null;
  selectedColumns: ReadonlySet<string>;
  onToggleColumn: (csvColumn: string) => void;
  candidateGroups: RemapCandidateGroup[] | null;
  remapAssignments: Readonly<Record<string, string>>;
  onAssignUnmatchedColumn: (
    csvColumn: string,
    canonicalId: string | null,
  ) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const selectedMatchedCanonicalIds = preview.matched
    .filter((m) => selectedColumns.has(m.csv_column))
    .map((m) => m.canonical_id);
  const remappedCount = Object.keys(remapAssignments).length;
  const selectedCount = selectedMatchedCanonicalIds.length + remappedCount;
  const claimedCanonicalIds = new Set<string>([
    ...selectedMatchedCanonicalIds,
    ...Object.values(remapAssignments),
  ]);

  return (
    <div className="space-y-3">
      <div className={cn(cardSurface, "px-3 py-2.5 space-y-1")}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Kohde · {targetName}
          </span>
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {fileName} · {preview.row_count.toLocaleString("fi-FI")} riviä
        </div>
      </div>

      <DataContextPicker value={dataContext} onChange={onDataContextChange} />

      {preview.matched.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
            Tunnistetut signaalit ({preview.matched.length})
          </div>
          <div className="space-y-1">
            {preview.matched.map((m) => (
              <div
                key={m.csv_column}
                className={cn(
                  cardSubtle,
                  "flex items-center gap-2 px-2.5 py-1.5 text-[11px]",
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedColumns.has(m.csv_column)}
                  onChange={() => onToggleColumn(m.csv_column)}
                  className="shrink-0"
                  aria-label={`Tuo sarake ${m.csv_column}`}
                />
                <span className="min-w-0 flex-1 truncate font-mono text-foreground">
                  {m.csv_column}
                </span>
                <span className="shrink-0 text-muted-foreground">→</span>
                <span className="min-w-0 flex-1 truncate">{m.name_fi}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {Math.round(m.confidence * 100)} %
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <UnmatchedColumnRemap
        unmatchedColumns={preview.unmatched}
        candidateGroups={candidateGroups}
        claimedIds={claimedCanonicalIds}
        assignments={remapAssignments}
        onAssign={onAssignUnmatchedColumn}
      />

      {preview.matched.length === 0 && (
        <div className="flex items-start gap-1.5 rounded-md bg-warning/10 px-2.5 py-2 text-[11px] text-warning">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
          Tiedostosta ei tunnistettu yhtään signaalia tälle laitteelle. Tarkista
          että valitsit oikean kohteen ja että sarakkeiden nimet ovat
          tunnistettavia.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border bg-surface text-xs font-medium py-2 hover:border-primary/30 transition"
        >
          Peruuta
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={selectedCount === 0}
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium py-2 hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Tuo {selectedCount} signaalia
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FileDrop({
  file,
  onFile,
  accept,
}: {
  file: File | null;
  onFile: (f: File | null) => void;
  accept: string;
}) {
  const [dragging, setDragging] = useState(false);

  function pickFromDataTransfer(dt: DataTransfer | null) {
    const f = dt?.files?.[0] ?? null;
    if (f) onFile(f);
  }

  return (
    <label
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        pickFromDataTransfer(e.dataTransfer);
      }}
      className={cn(
        "flex items-center gap-2 rounded-md border border-dashed bg-surface px-3 py-2.5 text-xs text-muted-foreground cursor-pointer transition",
        dragging
          ? "border-primary bg-primary/5 text-foreground"
          : "border-border hover:border-primary/40",
      )}
    >
      <Upload className="h-3.5 w-3.5" />
      {file ? (
        <span className="text-foreground truncate flex-1">{file.name}</span>
      ) : (
        <span className="flex-1">Vedä tiedosto tähän tai klikkaa</span>
      )}
      {file && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onFile(null);
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}
